/* eslint-disable react/jsx-no-duplicate-props */
import { faTint, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, Checkbox, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, InputAdornment, Paper, TextField,
 ToggleButton, ToggleButtonGroup
} from '@mui/material';

import {
  ButtonDelete, CustomIcon, CustomTooltip, Datepicker, FlexContainer, FormLine,
  ItemOption, Question, RequiredSelectHack, Text, TextError, Warn
} from 'components';
import { SkeletonForm } from 'components/Skeletons';
import { TextNoOptionsAvailable } from 'components/Text';
import debounce from 'debounce-promise';
import { useFetch, useStores } from 'hooks';
import { ModalProps } from 'hooks/ModalProvider';
import { BottleView, ContactDetail, FluidMovementView } from 'models';
import React, {
  Fragment, useCallback, useEffect, useState
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useForm, Controller } from 'react-hook-form';
import sanitizeHtml from 'sanitize-html';
import {
  FluidMovementsService, FluidsService, InstitutionsService, LegalEntityService, TrackDechetsService
} from 'services';
import shortid from 'shortid';
import { COUNTRY_CODE, DISTRIBUTOR_ROLES } from 'utils/constants';
import { FLUID_MOVEMENT_LIST } from 'utils/fluid_movement_constants';
import {
  DATE_FORMAT,
  DocumentHelper,
  FormHelper,
  UserHelper,
  formatDate,
  getSmallDescription, translate
} from 'utils/helpers';
import { SelectItem } from 'utils/types';
import { ModalHeader } from './ModalHeader';

const GROUP_INPUT = 'INPUT';
const GROUP_OUTPUT = 'OUTPUT';

const TYPE_INPUT = 'INPUT';
const TYPE_OUTPUT = 'OUTPUT';
const TYPE_RESET = 'RESET';
const TYPE_STOCK = 'STOCK';
const TYPE_HANDOVER = 'HANDOVER';
const TYPE_VALIDATION = 'VALIDATION';
const TYPE_RETRIEVAL = 'RETRIEVAL';
const TYPE_SALE = 'SALE';

const createMiniature = (document) => {
  const extension = DocumentHelper.getExtension(document);
  switch (extension) {
    case 'png':
    case 'jpg':
    case 'jpeg':
      return (
        <FlexContainer className="modalPicturesContainer">
          <img alt={document.name} key={shortid.generate()} src={document.base64Content} />
        </FlexContainer>
      );
    default:
      return null;
  }
};

export const CreateFluidMovementModal = ({
                                           onSubmit, onClose, movementId
                                         }: ModalProps<FluidMovementView> & {
  movementId?: string
}) => {
  const { fluidStore } = useStores();

  const {
    response: tdConnectionStatus,
    isLoading: isCheckingStatus
  } = useFetch(() => TrackDechetsService.getConnectionStatus(), { response: {}, isLoading: false }, [], false);
  const [uploadError, setUploadError] = useState('');
  const [linkedDocument, setLinkedDocument] = useState(null);
  const [searchTransportersTD, setSearchTransportersTD] = useState('');
  const [searchSuppliersOrRecipients, setSearchSuppliersOrRecipients] = useState('');
  const [suppliersOrRecipients, setSuppliersOrRecipients] = useState<SelectItem[]>([]);
  const [formattedBottleList, setFormattedBottleList] = useState<SelectItem[]>([]);
  const [isLoadingSuppliersOrRecipients, setIsLoadingSuppliersOrRecipients] = useState(false);
  const [isLoadingTransportersTD, setIsLoadingTransportersTD] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMovementGroup, setCurrentMovementGroup] = useState(GROUP_INPUT);
  const [fluidFilter, setFluidFilter] = useState<SelectItem>();
  const [isTransporterEmpty, setIsTransporterEmpty] = useState(false);

  const [contactDetails, setContactDetails] = useState<ContactDetail[]>([]);
  const [contactOptions, setContactOptions] = useState<SelectItem[]>([]);
  const [selectedContactIndex, setSelectedContactIndex] = useState(0);

  const isDistributor = UserHelper.hasAccessRight(DISTRIBUTOR_ROLES);
  const isFrance = UserHelper.getDefaultCountry()?.key === COUNTRY_CODE.FR;

    const {
        control, handleSubmit, setValue, watch, reset, formState: { errors }
    } = useForm({
        defaultValues: new FluidMovementView()
    });

    const formValues = watch();


    const getCurrentFluidMovementConfig = useCallback(() => {
        if (!formValues?.movementType?.value) {
            return FLUID_MOVEMENT_LIST.find(movement => movement.key === TYPE_INPUT);
        }
        return FLUID_MOVEMENT_LIST.find(movement => movement.key === formValues.movementType.value);
    }, [formValues?.movementType]);

    useEffect(() => {
        if (!movementId) {
            setValue('movementType', { value: TYPE_INPUT, label: TYPE_INPUT });
            setValue('movementDate', formatDate(new Date()));
            return;
        }
        setIsLoading(true);

        FluidMovementsService.getFluidMovement(movementId)
            .then((resp) => {
                resp.linkedDocument
                && setLinkedDocument({ name: resp.linkedDocument.name, base64Content: DocumentHelper.getPDFWithBase64(resp.linkedDocument) });

                const loadedData = { ...resp };
                loadedData.fluid = FormHelper.SELECT_ADAPTOR_TO_FRONT({ selectItem: resp.fluid });
                reset(loadedData);
            }).finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [movementId]);

    const setLinkedDocumentFromDocuments = (docs) => {
        setLinkedDocument(docs[0]);
    };

    useEffect(() => {
        if (fluidStore.isLoadingBottles) {
            return;
        }
        let bottles = fluidStore.bottleExtendedOptions;
        if (fluidFilter) {
            bottles = bottles.filter(bottle => bottle.fluid?.value === fluidFilter.value);
        }
        setFormattedBottleList(bottles.map(bottleItem => ({
            value: bottleItem.id,
            label: bottleItem.identifier,
            fluid: bottleItem.fluid
        })));
    }, [fluidFilter, fluidStore.bottleExtendedOptions, fluidStore.isLoadingBottles]);

    useEffect(() => {
        FluidsService.getBottleOptionsExtended({ movementType: formValues?.movementType })
            .then(bottleOptions => {
                let bottles = bottleOptions;
                if (fluidFilter) {
                    bottles = bottles.filter(bottle => bottle.fluid?.value === fluidFilter.value);
                }
                setFormattedBottleList(bottles.map(bottleItem => ({
                    value: bottleItem.id,
                    label: bottleItem.identifier,
                    fluid: bottleItem.fluid
                })));
            });
    }, [formValues?.movementType, fluidFilter]);


    useEffect(() => {
        const config = getCurrentFluidMovementConfig();
        if (!config?.supplierOrRecipient) {
            return;
        }

        LegalEntityService.getLegalEntitiesOptionsFiltered({
            types: [config.supplierOrRecipient]
        })
            .then(res => setSuppliersOrRecipients(res))
            .finally(() => setIsLoadingSuppliersOrRecipients(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues?.movementType?.value]);


    const changeContact = useCallback((option: SelectItem) => {
        if (!option) {
            return;
        }
        const contactIndex = Number(option.value);
        setSelectedContactIndex(contactIndex);
        let contactOption: ContactDetail = {};
        if (contactIndex < contactDetails.length) {
            contactOption = contactDetails[contactIndex];
        }
        setValue('contactName', contactOption.contactName ?? '');
        setValue('contactEmail', contactOption.contactEmails ?? '');
        setValue('contactPhone', contactOption.phoneNumber ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactDetails]);


    useEffect(() => {
        TrackDechetsService.getTrackDechetsContacts()
            .then(res => {
                setContactDetails(res);
                const processedContactOptions: SelectItem[] = [];
                let i = 0;
                if (res.length === 2) {

                    processedContactOptions.push({
                        value: i.toString(),
                        label: translate('modalFluidMovement.trackDechetsContact')
                    });
                    i++;
                }

                processedContactOptions.push({
                    value: i.toString(),
                    label: translate('modalFluidMovement.userContact')
                });
                i++;

                processedContactOptions.push({
                    value: i.toString(),
                    label: translate('modalFluidMovement.freeContact')
                });
                setContactOptions(processedContactOptions);
                setValue('contactName', res[0]?.contactName ?? '');
                setValue('contactEmail', res[0]?.contactEmails ?? '');
                setValue('contactPhone', res[0]?.phoneNumber ?? '');
            });

    }, []);


    useEffect(() => {
        // is Retrieval should be false by default for Retrieval movement
        if (!movementId && formValues?.movementType?.value === TYPE_RETRIEVAL) {
            setValue('isRetrievalBottle', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues?.movementType, movementId]);

    useEffect(() => {
        setIsTransporterEmpty(!formValues?.transporter);
    }, [formValues?.transporter]);


    const isCreating = !movementId;
    const isTypeInput = formValues?.movementType?.value === TYPE_INPUT;
    const isTypeStock = formValues?.movementType?.value === TYPE_STOCK;
    const isTypeHandover = formValues?.movementType?.value === TYPE_HANDOVER;
    const isTypeRetrieval = formValues?.movementType?.value === TYPE_RETRIEVAL;
    const isTypeOutput = formValues?.movementType?.value === TYPE_OUTPUT;
    const isTypeValidation = formValues?.movementType?.value === TYPE_VALIDATION;
    const isTypeSale = formValues?.movementType?.value === TYPE_SALE;
    const isGroupInput = getCurrentFluidMovementConfig()?.group === GROUP_INPUT;
    const needsFluidDisplay = (formValues?.bottle && formValues?.movementType?.value !== TYPE_RESET)
        || (formValues?.movementType?.value === TYPE_STOCK);
    const { hasDocument } = getCurrentFluidMovementConfig() || {};

    const onFormSubmit = (data) => {
        // Get the bottle data
        let bottle: BottleView;
        if (data.bottle && data.bottle.id) {
            // Use an existing bottle
            bottle = fluidStore.bottleExtendedOptions.find(bottleItem => bottleItem.id === data.bottle.id);
        } else {
            // Build a new bottle from the movement fields
            bottle = {
                ...new BottleView(),
                identifier: data.bottle
            };
        }

        let bottles: BottleView[] = [];
        if (data.bottles?.length === 1) {
            bottle = fluidStore.bottleExtendedOptions.find(bottleItem => bottleItem.id === data.bottles[0].id);
        } else if (data.bottles?.length > 1) {
            bottles = data.bottles.map(b => fluidStore.bottleExtendedOptions.find(bottleOption => bottleOption.id === b.id));
        }

        const request: FluidMovementView = {
            ...data,
            hashId: movementId,
            movementType: { value: getCurrentFluidMovementConfig().key, label: '' },
            bottle,
            bottles,
            fluid: data.fluid ? FormHelper.SELECT_ADAPTOR_TO_BACK(data.fluid) : null,
            theoricalAmount: Number(data.theoricalAmount),
            measuredAmount: Number(data.measuredAmount),
            maximumCapacity: Number(data.maximumCapacity),
            volume: data.volume && Number(data.volume),
            contact: {
                contactName: data.contactName,
                contactEmails: data.contactEmail,
                phoneNumber: data.contactPhone
            }
        };

        if (linkedDocument && hasDocument) {
            request.linkedDocument = {
                ...linkedDocument,
                base64Content: DocumentHelper.getDocumentWithoutBase64(linkedDocument)
            };
        }
        onSubmit(request);
    };

    const getAsyncTransportersOptions = useCallback(inputValue => new Promise((resolve) => {
        if (!inputValue) {
            setIsLoadingTransportersTD(false);
            return;
        }
        setIsLoadingTransportersTD(true);
        if (inputValue.length < 3) {
            return;
        }

        InstitutionsService.getInstitutionsTrackDechets(inputValue)
            .then(instiList => resolve(instiList))
            .finally(() => setIsLoadingTransportersTD(false));
    }), []);

    const getAsyncSuppliersOrRecipientsOptions = useCallback(inputValue => new Promise((resolve) => {
        if (!inputValue) {
            setIsLoadingSuppliersOrRecipients(false);
            return;
        }
        setIsLoadingSuppliersOrRecipients(true);
        if (inputValue.length < 3) {
            return;
        }

        const config = getCurrentFluidMovementConfig();
        LegalEntityService.getLegalEntitiesOptionsFiltered({
            freeSearch: inputValue,
            types: [config?.supplierOrRecipient]
        })
            .then(res => resolve(res))
            .finally(() => setIsLoadingSuppliersOrRecipients(false));
    }), [getCurrentFluidMovementConfig]);

    const debouncedTransporterOptions = debounce(getAsyncTransportersOptions, 500);
    const debouncedSuppliersOrRecipientsOptions = debounce(getAsyncSuppliersOrRecipientsOptions, 500);

    const CustomOption = ({ data, innerProps }) => (
        <ItemOption {...innerProps} isInsideSelect>
            <article>
                <Text fontSize="2rem" margin="0">{data.label}</Text>
                <Text color="var(--grey)" fontSize="1.4rem" margin="0">{data.fluid && `${translate('common.fluid')} : ${data.fluid.label}`}</Text>
            </article>
        </ItemOption>
    );


    const resetFields = useCallback(() => {
        setValue('supplierOrRecipient', null);
        setValue('bottles', []);
        setValue('bottle', null);
        setValue('fluid', null);
        setValue('theoricalAmount', null);
        setValue('measuredAmount', null);
        setValue('volume', null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const displayNoOptionAvailable = useCallback((search) => {
        if (!search || search.length < 3) {
            return (
                <TextNoOptionsAvailable>{translate('modalFluidMovement.startSearch')}</TextNoOptionsAvailable>
            );
        }
        return (
            <TextNoOptionsAvailable>{translate('modalFluidMovement.noInstitution')}</TextNoOptionsAvailable>
        );
    }, []);

    const getModalMinSizeStyle = useCallback(() => {
        let minWidth = 700;
        let minHeight = 650;

        if (isDistributor) {
            minWidth = 900;
        }
        if (movementId) {
            minWidth = 400;
            minHeight = 500;
        }
        if (isMobileOnly) {
            minWidth = 200;
            minHeight = 500;
        }
        return { minWidth: `${minWidth}px`, minHeight: `${minHeight}px` };
    }, [isDistributor, movementId]);

    if (isCheckingStatus || !tdConnectionStatus || isLoading) {
        return (
            <SkeletonForm />
        );
    }

    return (
        <form autoComplete="off" style={getModalMinSizeStyle()} onSubmit={handleSubmit(onFormSubmit)}>
            <ModalHeader onClose={onClose}>
                <CustomIcon icon={<FontAwesomeIcon icon={faTint} />} marginRight="1rem" />
                {movementId ? translate('modalFluidMovement.editFluidMovement') : translate('modalFluidMovement.newFluidMovement')}
            </ModalHeader>

            <DialogContent style={{ paddingTop: '10px' }}>
                <FlexContainer flexDirection="column">
                    {!movementId && !tdConnectionStatus.isValid && isFrance && (
                        <Warn maxWidth="600px">{tdConnectionStatus.message}</Warn>
                    )}

                    {!movementId && (
                        <>
                            <Question label={translate('modalFluidMovement.fluidMovementGroup')}>
                                <ToggleButtonGroup
                                    aria-label={translate('modalFluidMovement.fluidMovementGroup')}
                                    data-cy="selectType"
                                    exclusive
                                    size="medium"
                                    value={currentMovementGroup}
                                    onChange={(event, value) => {
                                        setCurrentMovementGroup(value);
                                        resetFields();
                                        if (value === GROUP_INPUT) {
                                            setValue('movementType', { value: TYPE_INPUT, label: TYPE_INPUT });
                                        } else {
                                            setValue('movementType', { value: TYPE_OUTPUT, label: TYPE_OUTPUT });
                                        }
                                    }}
                                >
                                    <ToggleButton
                                        aria-label="input"
                                        data-cy="input"
                                        key={shortid.generate()}
                                        value={GROUP_INPUT}
                                    >
                                        {translate('modalFluidMovement.input')}
                                    </ToggleButton>
                                    <ToggleButton
                                        aria-label="output"
                                        data-cy="output"
                                        key={shortid.generate()}
                                        value={GROUP_OUTPUT}
                                    >
                                        {translate('modalFluidMovement.output')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Question>
                            <Question label={translate(currentMovementGroup === GROUP_INPUT
                                ? 'modalFluidMovement.fluidMovementInput'
                                : 'modalFluidMovement.fluidMovementOutput')}
                            >
                                <ToggleButtonGroup
                                    aria-label={translate('modalFluidMovement.fluidMovementGroup')}
                                    data-cy="selectType"
                                    exclusive
                                    size="medium"
                                    value={formValues?.movementType?.value}
                                    onChange={(event, value) => {
                                        if (value === null) return;
                                        setValue('movementType', { value, label: value });
                                        resetFields();
                                    }}
                                >
                                    {FLUID_MOVEMENT_LIST
                                        .filter(movement => movement.group === currentMovementGroup)
                                        .filter(movement => movement.availableToOperator || isDistributor)
                                        .map(movement => (
                                            <ToggleButton
                                                aria-label={movement.label}
                                                data-cy={movement.key}
                                                key={shortid.generate()}
                                                value={movement.key}
                                            >
                        <span dangerouslySetInnerHTML={
                            { __html: sanitizeHtml(translate(isDistributor && movement.labelDistributor ? movement.labelDistributor : movement.label)) }
                        }
                        />
                                            </ToggleButton>
                                        ))}
                                </ToggleButtonGroup>
                            </Question>
                        </>
                    )}

                    {isGroupInput && !movementId && (
                        <Fragment>
                            {isTypeInput && !isDistributor && (
                                <Warn maxWidth="600px">{translate('warnings.movement.bottleRecup')}</Warn>
                            )}
                            <Controller
                                control={control}
                                name="bottle"
                                render={({ field }) => (
                                    <TextField
                                        error={!!errors.bottle}
                                        label={translate('modalFluidMovement.bottleIdentifier')}
                                        required
                                        style={{ margin: '5px 0' }}
                                        {...field}
                                        inputProps={{ pattern: '.*\\S.*' }}
                                    />
                                )}
                            />

                            {isTypeStock && (
                                <FlexContainer alignItems="center">
                                    <Controller
                                        control={control}
                                        name="isRetrievalBottle"
                                        render={({ field }) => (
                                            <ToggleButtonGroup
                                                aria-label="isRetrievalBottle"
                                                data-cy="isRetrievalBottle"
                                                exclusive
                                                size="small"
                                                value={field.value}
                                                onChange={(event, value) => {
                                                    if (value === null) return;
                                                    field.onChange(value);
                                                }}
                                            >
                                                <ToggleButton
                                                    aria-label={translate('bottles.RETRIEVAL_BOTTLE')}
                                                    data-cy="yes"
                                                    key={shortid.generate()}
                                                    value={true}
                                                >
                                                    {translate('bottles.RETRIEVAL_BOTTLE')}
                                                </ToggleButton>
                                                <ToggleButton
                                                    aria-label={translate('bottles.TRANSFER_BOTTLE')}
                                                    data-cy="no"
                                                    key={shortid.generate()}
                                                    value={false}
                                                >
                                                    {translate('bottles.TRANSFER_BOTTLE')}
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        )}
                                    />
                                </FlexContainer>
                            )}
                        </Fragment>
                    )}

                    {isTypeRetrieval && !movementId && (
                        <FlexContainer alignItems="center">
                            <Controller
                                control={control}
                                name="isRetrievalBottle"
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        aria-label="isRetrievalBottle"
                                        data-cy="isRetrievalBottle"
                                        exclusive
                                        size="small"
                                        value={field.value}
                                        onChange={(event, value) => {
                                            if (value === null) return;
                                            field.onChange(value);
                                        }}
                                    >
                                        <ToggleButton
                                            aria-label={translate('bottles.RETRIEVAL_BOTTLE')}
                                            data-cy="yes"
                                            key={shortid.generate()}
                                            value={true}
                                        >
                                            {translate('bottles.RETRIEVAL_BOTTLE')}
                                        </ToggleButton>
                                        <ToggleButton
                                            aria-label={translate('bottles.NEW_OR_REGENERATED')}
                                            data-cy="no"
                                            key={shortid.generate()}
                                            value={false}
                                        >
                                            {translate('bottles.NEW_OR_REGENERATED')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            />
                        </FlexContainer>
                    )}

                    {getCurrentFluidMovementConfig()?.supplierOrRecipient && (
                        <Question label={translate(getCurrentFluidMovementConfig().supplierOrRecipientLabel)}>
                            <Controller
                                control={control}
                                name="supplierOrRecipient"
                                render={({ field }) => (
                                    <AsyncSelect
                                        cacheOptions
                                        defaultOptions={suppliersOrRecipients}
                                        getOptionLabel={option => option.label}
                                        getOptionValue={option => option.value}
                                        id="selectInstitution"
                                        inputValue={searchSuppliersOrRecipients}
                                        isClearable
                                        isLoading={isLoadingSuppliersOrRecipients}
                                        isSearchable
                                        loadingMessage={() => translate('modalFluidMovement.loadingInstitutions')}
                                        loadOptions={debouncedSuppliersOrRecipientsOptions}
                                        menuPlacement={isMobileOnly ? 'top' : 'bottom'}
                                        menuPortalTarget={document.body}
                                        noOptionsMessage={() => displayNoOptionAvailable(searchSuppliersOrRecipients)}
                                        placeholder={translate('common.searchByNameSiret')}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                        value={field.value || null}
                                        onChange={field.onChange}
                                        onInputChange={value => setSearchSuppliersOrRecipients(value)}
                                    />
                                )}
                            />
                            {isTypeOutput && !isTypeHandover && <RequiredSelectHack value={formValues?.supplierOrRecipient || ''} />}
                            {isTypeOutput && tdConnectionStatus?.isValid && (
                                <Text color="var(--orange)" fontSize="14px">{translate('warnings.tdConnection.recipient')}</Text>
                            )}
                        </Question>
                    )}

                    {isTypeOutput && !movementId && tdConnectionStatus?.isValid && (
                        <Question label={`${translate('modalFluidMovement.transporter')} :`}>
                            <Controller
                                control={control}
                                name="transporter"
                                render={({ field }) => (
                                    <AsyncSelect
                                        cacheOptions
                                        getOptionLabel={option => `${option.name} (${option.siret})`}
                                        getOptionValue={option => option.siret}
                                        id="selectTransporter"
                                        inputValue={searchTransportersTD}
                                        isClearable
                                        isLoading={isLoadingTransportersTD}
                                        isSearchable
                                        loadingMessage={() => translate('modalFluidMovement.loadingInstitutions')}
                                        loadOptions={debouncedTransporterOptions}
                                        menuPlacement={isMobileOnly ? 'top' : 'bottom'}
                                        menuPortalTarget={document.body}
                                        noOptionsMessage={() => displayNoOptionAvailable(searchTransportersTD)}
                                        placeholder={translate('common.searchByNameSiret')}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                        value={field.value || null}
                                        onChange={field.onChange}
                                        onInputChange={value => setSearchTransportersTD(value)}
                                    />
                                )}
                            />
                            {isTransporterEmpty && (
                                <>
                                    <Text color="var(--orange)" fontSize="14px">{translate('modalFluidMovement.notTransporterWarning')}</Text>
                                    <Controller
                                        control={control}
                                        name="transporterImat"
                                        render={({ field }) => (
                                            <TextField
                                                error={!!errors.transporterImat}
                                                label={translate('common.transporterImat')}
                                                required
                                                {...field}
                                            />
                                        )}
                                    />
                                </>
                            )}
                        </Question>
                    )}

                    {!isGroupInput && !movementId && (
                        <Fragment>
                            <Question label={`${translate('modalBottle.chooseFluid')} :`}>
                                <Select
                                    id="selectFluidFilter"
                                    isClearable
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                    options={fluidStore.fluidOptions}
                                    placeholder={translate('common.searchByFluidType')}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                    value={fluidFilter}
                                    onChange={setFluidFilter}
                                />
                            </Question>
                        </Fragment>
                    )}

                    {!isGroupInput && !isTypeSale && !movementId && (
                        <Question label={`${translate('modalFluidMovement.chooseBottle')} :`}>
                            <Controller
                                control={control}
                                name="bottle"
                                render={({ field }) => (
                                    <Select
                                        components={{ Option: CustomOption }}
                                        id="selectBottle"
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                        options={formattedBottleList}
                                        placeholder={translate('common.searchByBottle')}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                        value={field.value ? {
                                            value: field.value.id,
                                            label: field.value.identifier
                                        } : null}
                                        onChange={(select) => {
                                            if (select) {
                                                const matchBottle = fluidStore.bottleExtendedOptions.find(bottleItem => bottleItem.id === select.value);
                                                if (matchBottle) {
                                                    field.onChange(matchBottle);
                                                    setValue('fluid', FormHelper.SELECT_ADAPTOR_TO_FRONT({ selectItem: matchBottle.fluid }));
                                                    setValue('theoricalAmount', matchBottle.theoricalAmount);
                                                    setValue('measuredAmount', Number(matchBottle.measuredAmount));
                                                    matchBottle.volume && setValue('volume', Number(matchBottle.volume));
                                                }
                                            } else {
                                                resetFields();
                                            }
                                        }}
                                    />
                                )}
                            />
                            <RequiredSelectHack value={formValues?.bottle || ''} />
                        </Question>
                    )}

                    {isTypeSale && !movementId && (
                        <Question label={`${translate('modalFluidMovement.chooseBottles')} :`}>
                            <Controller
                                control={control}
                                name="bottles"
                                render={({ field }) => (
                                    <Select
                                        components={{ Option: CustomOption }}
                                        id="selectBottle"
                                        isClearable
                                        isMulti
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                        options={formattedBottleList}
                                        placeholder={translate('common.searchByBottle')}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                        value={field.value ? field.value.map(b => ({
                                            value: b.id,
                                            label: b.identifier
                                        })) : null}
                                        onChange={(select) => {
                                            if (select) {
                                                field.onChange(select.map((s: SelectItem) => ({
                                                    id: s.value,
                                                    identifier: s.label
                                                })));
                                                if (select?.length === 1) {
                                                    const matchBottle = fluidStore.bottleExtendedOptions.find(bottleItem => bottleItem.id === select[0].value);
                                                    if (matchBottle) {
                                                        setValue('bottle', matchBottle);
                                                        setValue('fluid', FormHelper.SELECT_ADAPTOR_TO_FRONT({ selectItem: matchBottle.fluid }));
                                                        setValue('theoricalAmount', matchBottle.theoricalAmount);
                                                        setValue('measuredAmount', Number(matchBottle.measuredAmount));
                                                        matchBottle.volume && setValue('volume', Number(matchBottle.volume));
                                                    }
                                                }
                                            } else {
                                                resetFields();
                                            }
                                        }}
                                    />
                                )}
                            />
                            <RequiredSelectHack value={formValues?.bottles || ''} />
                            {formValues?.bottles?.length > 1 && (
                                <>
                                    <br />
                                    <Warn maxWidth="600px">{translate('warnings.movement.bottlesDefaultQuantity')}</Warn>
                                </>
                            )}
                        </Question>
                    )}

                    {isGroupInput ? (
                        <Fragment>
                            {!movementId ? (
                                <Fragment>
                                    <Question label={`${translate('modalBottle.chooseFluid')} :`}>
                                        <Controller
                                            control={control}
                                            name="fluid"
                                            render={({ field }) => (
                                                <Select
                                                    id="selectFluid"
                                                    isClearable
                                                    isDisabled={!!movementId}
                                                    isSearchable
                                                    menuPortalTarget={document.body}
                                                    noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                                    options={fluidStore.fluidOptions}
                                                    placeholder={translate('common.searchByFluidType')}
                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                                    value={(field.value && field.value.value) ? field.value : ''}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        {isTypeInput && (
                                            <RequiredSelectHack value={formValues?.fluid || ''} />
                                        )}
                                    </Question>

                                    {(isTypeInput || (isTypeRetrieval && !formValues?.isRetrievalBottle)) && (
                                        <Fragment>
                                            <Controller
                                                control={control}
                                                name="isNewFluid"
                                                render={({ field }) => (
                                                    <ToggleButtonGroup
                                                        aria-label={translate('common.fluidType')}
                                                        data-cy="isNewFluid"
                                                        exclusive
                                                        size="small"
                                                        style={{ marginTop: '1rem' }}
                                                        value={field.value}
                                                        onChange={(event, value) => {
                                                            if (value === null) return;
                                                            field.onChange(value);
                                                        }}
                                                    >
                                                        <ToggleButton
                                                            aria-label={translate('fluids.newFluid')}
                                                            data-cy="newFluid"
                                                            key={shortid.generate()}
                                                            value={true}
                                                        >
                                                            {translate('fluids.newFluid')}
                                                        </ToggleButton>
                                                        <ToggleButton
                                                            aria-label={translate('fluids.regeneratedFluid')}
                                                            data-cy="regeneratedFluid"
                                                            key={shortid.generate()}
                                                            value={false}
                                                        >
                                                            {translate('fluids.regeneratedFluid')}
                                                        </ToggleButton>
                                                    </ToggleButtonGroup>
                                                )}
                                            />

                                            {isTypeInput && (
                                                <>
                                                    <FlexContainer alignItems="center">
                                                        <Text margin="0 2rem 0 0">{translate('fluids.boughtInOperatorCountry')}</Text>
                                                        <Controller
                                                            control={control}
                                                            name="boughtInOperatorCountry"
                                                            render={({ field }) => (
                                                                <ToggleButtonGroup
                                                                    aria-label={translate('fluids.boughtInOperatorCountry')}
                                                                    data-cy="boughtInOperatorCountry"
                                                                    exclusive
                                                                    size="small"
                                                                    style={{ marginTop: '1rem' }}
                                                                    value={field.value}
                                                                    onChange={(event, value) => {
                                                                        if (value === null) return;
                                                                        field.onChange(value);
                                                                    }}
                                                                >
                                                                    <ToggleButton
                                                                        aria-label={translate('common.yes')}
                                                                        data-cy="yes"
                                                                        key={shortid.generate()}
                                                                        value={true}
                                                                    >
                                                                        {translate('common.yes')}
                                                                    </ToggleButton>
                                                                    <ToggleButton
                                                                        aria-label={translate('common.no')}
                                                                        data-cy="no"
                                                                        key={shortid.generate()}
                                                                        value={false}
                                                                    >
                                                                        {translate('common.no')}
                                                                    </ToggleButton>
                                                                </ToggleButtonGroup>
                                                            )}
                                                        />
                                                    </FlexContainer>
                                                    {!formValues?.boughtInOperatorCountry && (
                                                        <Warn maxWidth="600px">{translate('warnings.movement.boughtInFrance')}</Warn>
                                                    )}
                                                </>
                                            )}
                                        </Fragment>
                                    )}
                                </Fragment>
                            ) : needsFluidDisplay && (
                                <Text color="var(--grey)">
                                    {`${translate('common.fluid')} : ${(formValues?.fluid && formValues.fluid.label) || '-'}`}
                                </Text>
                            )}

                            {(isTypeInput || isTypeValidation)
                                ? (
                                    <FormLine>
                                        <Controller
                                            control={control}
                                            name="theoricalAmount"
                                            render={({ field }) => (
                                                <TextField
                                                    disabled={isTypeValidation && !!movementId}
                                                    error={!!errors.theoricalAmount}
                                                    label={translate(getCurrentFluidMovementConfig()?.theoricalAmountLabel)}
                                                    required
                                                    {...field}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                                    }}
                                                    inputProps={{
                                                        step: '0.01',
                                                        min: 0,
                                                        type: 'number'
                                                    }}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value === '' ? null : Number(e.target.value);
                                                        field.onChange(value);
                                                    }}
                                                />
                                            )}
                                        />
                                        <CustomTooltip text={translate('modalFluidMovement.tooltipTheorical')} />
                                    </FormLine>
                                ) : !isGroupInput && formValues?.bottle && (
                                <Text color="var(--grey)">{`${translate('common.boughtQuantity')} : ${formValues.theoricalAmount || '0'}${translate('common.weightUnit')}`}</Text>
                            )}
                        </Fragment>
                    ) : formValues?.bottle && formValues?.movementType?.value !== TYPE_RESET && (
                        <Text
                            color="var(--grey)"
                        >
                            {`${translate('common.fluid')} : ${(formValues?.fluid && formValues.fluid.label) || '-'}`}
                        </Text>
                    )}

                    {!movementId && !isGroupInput && (formValues?.bottle || formValues?.bottles?.length === 1) && (
                        <Warn maxWidth="600px">{translate('modalFluidMovement.confirmAmount')}</Warn>
                    )}

                    {(isGroupInput || isTypeValidation || (!isGroupInput && (formValues?.bottle || formValues?.bottles?.length === 1))) && (
                        <FormLine>
                            <Controller
                                control={control}
                                name="measuredAmount"
                                render={({ field: { onChange, value, ...restField } }) => (
                                    <TextField
                                        error={!!errors.measuredAmount}
                                        label={translate(isDistributor && getCurrentFluidMovementConfig()?.measuredAmountLabelDistributor
                                            ? getCurrentFluidMovementConfig().measuredAmountLabelDistributor
                                            : getCurrentFluidMovementConfig()?.measuredAmountLabel)}
                                        name="measuredAmount"
                                        required={!isTypeHandover}
                                        {...restField}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                        }}
                                        inputProps={{
                                            step: '0.01',
                                            min: 0,
                                            type: 'number'
                                        }}
                                        style={{ marginTop: '.5rem' }}
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            if (e.target.value === '' || !isNaN(Number(e.target.value))) {
                                                onChange(e.target.value);
                                            }
                                        }}
                                    />
                                )}
                            />
                            {isTypeInput && (
                                <CustomTooltip text={translate('modalFluidMovement.tooltipMeasured')} />
                            )}
                        </FormLine>
                    )}

                    {formValues?.bottle && (
                        <Controller
                            control={control}
                            name="volume"
                            render={({ field: { onChange, value, ...restField } }) => (
                                <TextField
                                    disabled={(movementId || !isGroupInput) && !!formValues.bottle.volume}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">L</InputAdornment>
                                    }}
                                    inputProps={{
                                        step: '0.01',
                                        min: 0,
                                        type: 'number'
                                    }}
                                    label={translate('common.bottleVolume')}
                                    style={{ marginTop: '.5rem' }}
                                    {...restField}
                                    value={value ?? ''}
                                    onChange={(e) => {
                                        if (e.target.value === '' || !isNaN(Number(e.target.value))) {
                                            onChange(e.target.value);
                                        }
                                    }}
                                />
                            )}
                        />
                    )}

                    {isTypeOutput && isCreating && tdConnectionStatus?.isValid && (
                        <Fragment>
                            <Question label={`${translate('modalFluidMovement.selectContact')} :`}>
                                <Select
                                    id="selectContact"
                                    menuPortalTarget={document.body}
                                    noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                    options={contactOptions}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                    value={contactOptions[selectedContactIndex]}
                                    onChange={changeContact}
                                />
                            </Question>
                            <Controller
                                control={control}
                                name="contactName"
                                render={({ field }) => (
                                    <TextField
                                        disabled={selectedContactIndex < contactDetails.length}
                                        label={translate('modalFluidMovement.contactName')}
                                        style={{ marginTop: '.5rem' }}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="contactEmail"
                                render={({ field }) => (
                                    <TextField
                                        disabled={selectedContactIndex < contactDetails.length}
                                        label={translate('modalFluidMovement.contactEmail')}
                                        style={{ marginTop: '.5rem' }}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="contactPhone"
                                render={({ field }) => (
                                    <TextField
                                        disabled={selectedContactIndex < contactDetails.length}
                                        label={translate('modalFluidMovement.contactPhone')}
                                        style={{ marginTop: '.5rem' }}
                                        {...field}
                                    />
                                )}
                            />
                        </Fragment>
                    )}

                    {(isTypeHandover || isTypeValidation) && (
                        <Question label={`${translate('modalBottle.comment')} :`}>
                            <Controller
                                control={control}
                                name="comment"
                                render={({ field }) => (
                                    <TextField
                                        InputLabelProps={{ required: false }}
                                        label={translate('modalBottle.commentLabel')}
                                        multiline
                                        required
                                        rows={3}
                                        {...field}
                                    />
                                )}
                            />
                        </Question>
                    )}

                    {hasDocument && (
                        <Question label={translate(getCurrentFluidMovementConfig()?.documentLabel)}>
                            <input
                                accept={isTypeRetrieval ? 'application/pdf' : 'image/*,application/pdf'}
                                type="file"
                                onChange={event => DocumentHelper.handleFileUpload({
                                    callback: setLinkedDocumentFromDocuments, callbackError: setUploadError, event, needResize: !isTypeRetrieval
                                })}
                            />

                            {uploadError && <TextError>{uploadError}</TextError>}

                            {linkedDocument && (
                                <Grid container direction="row" style={{ marginTop: 10 }}>
                                    <Grid item xs={11}>
                                        <Paper style={{ padding: 10 }} variant="outlined">
                                            <Text fontWeight="bold" margin="5px 0px">{translate('common.importedFile')}</Text>
                                            <a download={linkedDocument.name} href={linkedDocument.base64Content}>{getSmallDescription(linkedDocument.name, 40)}</a>
                                        </Paper>
                                    </Grid>
                                    {!movementId && (
                                        <Grid item style={{ alignSelf: 'center' }} xs={1}>
                                            <ButtonDelete onClick={() => setLinkedDocument(null)}>
                                                <FontAwesomeIcon icon={faTrashAlt} size="sm" />
                                            </ButtonDelete>
                                        </Grid>
                                    )}
                                </Grid>
                            )}

                            {linkedDocument && createMiniature(linkedDocument)}
                        </Question>
                    )}

                    <Controller
                        control={control}
                        name="movementDate"
                        render={({ field }) => (
                            <Datepicker
                                dataCy="createFluidMovementDate"
                                disableFuture
                                format={DATE_FORMAT}
                                label="common.operationDate"
                                required
                                style={{ marginTop: '.5rem' }}
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />

                    {isTypeOutput && isCreating && tdConnectionStatus?.isValid && (
                        <FormControl>
                            <FormLine>
                                <Controller
                                    control={control}
                                    name="ignoreTd"
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={<Checkbox checked={field.value} onChange={field.onChange} />}
                                            key="active"
                                            label={translate('common.ignoreTd')}
                                        />
                                    )}
                                />
                            </FormLine>
                        </FormControl>
                    )}
                </FlexContainer>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{translate('button.cancel')}</Button>
                <Button
                    color="primary"
                    type="submit"
                >
                    {(isTypeOutput && !movementId && tdConnectionStatus?.isValid && !formValues?.ignoreTd) ? translate('button.saveTD') : translate('button.save')}
                </Button>
            </DialogActions>
        </form>
    );
};